import {
  UserCheck,
  Eye,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

/**
 * "Personne de ménage attitrée", premium reassurance section.
 * Highlights the unique value of having a single, dedicated person take care
 * of the property, which deters theft, catches damage instantly, and triggers
 * platform insurance claims (AirCover up to 1M€) seamlessly.
 *
 * Used on every city page (after ServicesGrid) and on /services.
 */
const DedicatedHousekeeper = () => {
  return (
    <section
      className="py-20 lg:py-28 bg-cream"
      data-testid="dedicated-housekeeper-section"
    >
      <div className="container-tm">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left, visual / image */}
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-lift aspect-[4/5]">
                <img
                  src="/images/menage-linge.jpg"
                  alt="Personne de ménage attitrée préparant le linge hôtelier dans un logement Terre et Mer"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {/* Subtle navy overlay for depth */}
                <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent pointer-events-none" />
              </div>

              {/* Floating badge, AirCover */}
              <div className="hidden md:flex absolute -bottom-6 -right-6 bg-ink text-white rounded-2xl p-5 shadow-lift items-center gap-4 max-w-[260px]">
                <div className="w-11 h-11 rounded-full bg-gold flex items-center justify-center shrink-0">
                  <ShieldCheck size={20} strokeWidth={1.8} className="text-ink" />
                </div>
                <div>
                  <p className="font-serif text-base leading-tight">AirCover 1 M€</p>
                  <p className="text-[11px] font-sans text-white/60 mt-1">
                    Assurance plateformes activée pour vous
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right, copy */}
          <div className="lg:col-span-7 order-1 lg:order-2">
            <span className="overline inline-flex items-center gap-2 text-gold">
              <Sparkles size={12} strokeWidth={2} />
              Le petit + Terre et Mer
            </span>

            <h2 className="mt-5 font-serif font-light text-ink text-4xl md:text-5xl lg:text-[56px] leading-[1.04] tracking-tight">
              Une personne de ménage
              <span className="italic text-gold"> attitrée</span> à votre logement.
            </h2>

            <p className="mt-7 font-sans text-base md:text-lg text-ink/70 leading-relaxed">
              Chez Terre et Mer, le même membre de notre équipe de ménage prend soin
              de votre bien à <span className="text-ink font-semibold">chaque rotation</span>.
              Pas de roulement, pas de prestataires anonymes, la même personne,
              séjour après séjour.
            </p>

            {/* 3 reassurance bullets */}
            <ul className="mt-10 space-y-6">
              <Bullet
                icon={UserCheck}
                title="Une connaissance intime de votre logement"
                text="Elle connaît chaque meuble, chaque objet, chaque détail. Cette familiarité crée un soin personnel, votre bien est traité comme le sien."
              />
              <Bullet
                icon={Eye}
                title="La moindre anomalie est détectée immédiatement"
                text="Un objet déplacé, manquant, une trace inhabituelle : aucune dégradation ni vol ne passe inaperçu. Vous êtes prévenu sous quelques heures."
              />
              <Bullet
                icon={ShieldCheck}
                title="Nous activons l'assurance plateforme pour vous"
                text="En cas d'incident, nous documentons les dommages avec photos et contactons Airbnb, Booking ou Vrbo. Vous n'avez rien à gérer, AirCover couvre jusqu'à 1 000 000 €."
              />
            </ul>

            {/* Bottom italic reassurance line */}
            <div className="mt-10 pt-8 border-t border-gold/30">
              <p className="font-serif italic text-ink/75 text-base md:text-lg leading-relaxed">
                Votre bien est entre les mains d'une personne qui le connaît mieux que
                personne. C'est notre engagement, et c'est rare dans la profession.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Bullet = ({ icon: Icon, title, text }) => (
  <li className="flex items-start gap-5">
    <div className="w-12 h-12 rounded-xl bg-gold/15 border border-gold/25 flex items-center justify-center shrink-0 mt-0.5">
      <Icon size={20} strokeWidth={1.8} className="text-gold" />
    </div>
    <div>
      <p className="font-serif text-lg md:text-xl text-ink leading-snug">{title}</p>
      <p className="mt-2 font-sans text-sm md:text-base text-ink/65 leading-relaxed">
        {text}
      </p>
    </div>
  </li>
);

export default DedicatedHousekeeper;
